import { listAllPages, sdkDatesToStrings, sdkStringsToDates } from "@/api/utils/sdk";
import { atmBackConfiguration } from "@/setup/restAtmBackClient";
import { camelToSnakeCase } from "@/utils/helpers";
import {
  BuildingsApi,
  CasesApi,
  SitesApi,
  SpacesApi,
  StoreysApi,
  ZonesApi,
} from "@socotec.io/atm-back-sdk";

const casesApi = new CasesApi(atmBackConfiguration);
const sitesApi = new SitesApi(atmBackConfiguration);
const buildingsApi = new BuildingsApi(atmBackConfiguration);
const storeysApi = new StoreysApi(atmBackConfiguration);
const spacesApi = new SpacesApi(atmBackConfiguration);
const zonesApi = new ZonesApi(atmBackConfiguration);

export const ASSET_PAGE_SIZE = 500;

// Building date fields: the SDK exposes them as Date, but the BuildingAsset
// pinia-orm model stores strings — convert on the way in and out.
const BUILDING_DATE_FIELDS = [
  "visitDate",
  "certificationDate",
  "depositBuildingPermitDate",
  "buildPermitDate",
];

const buildingFromSdk = (building) => sdkDatesToStrings(building, { dates: BUILDING_DATE_FIELDS });

// L.B 23/07/26 The AOS filters use double-underscore lookups (path__startswith,
// aos_items__in) and `case` is exposed as `_case` in the SDK; the generic
// snake→camel helper mangles both, so map them explicitly.
const toAosListParams = ({
  case: caseUuid,
  parent,
  pathStartswith,
  pageSize = ASSET_PAGE_SIZE,
} = {}) => ({
  _case: caseUuid,
  parent,
  pathStartswith,
  pageSize,
});

export const fetchCaseAssets = async ({ caseInfo } = {}) => {
  return listAllPages((page) =>
    casesApi.aosObservables({ uuid: caseInfo, page, pageSize: ASSET_PAGE_SIZE }),
  );
};

export const addCaseAssets = async (formattedAssetsData, caseInfoUuid) => {
  const response = await casesApi.attach({
    uuid: caseInfoUuid,
    attachAssetsRequest: formattedAssetsData,
  });
  return response.results;
};

export const removeCaseAssetsInAtm = async (selection, caseInfoUuid) => {
  const detachAssets = {
    sites: selection.site ?? [],
    buildings: selection.building ?? [],
    storeys: selection.storey ?? [],
    zones: selection.zone ?? [],
    spaces: selection.space ?? [],
  };
  // Echoes the removed uuids per structure type (front deletes local records).
  return await casesApi.detach({ uuid: caseInfoUuid, detachAssetsRequest: detachAssets });
};

export const bulkCreateBuildings = async (buildings) => {
  const response = await buildingsApi.bulkCreate({
    buildingBulkCreateRequest: { buildings },
  });
  return response.results.map(buildingFromSdk);
};

export const fetchSites = async (aosSites = [], params = {}) => {
  const listParams = toAosListParams(params);
  if (aosSites.length) listParams.aosItemsIn = aosSites;
  return listAllPages((page) => sitesApi.list({ ...listParams, page }));
};

export const fetchBuildings = async (params = {}) => {
  const listParams = toAosListParams(params);
  const buildings = await listAllPages((page) => buildingsApi.list({ ...listParams, page }));
  return buildings.map(buildingFromSdk);
};

export const siteInfoByCase = async ({ uuid, case: caseUuid }) => {
  return await sitesApi.info({ uuid, _case: caseUuid });
};

export const buildingInfoByCase = async ({ uuid, case: caseUuid }) => {
  return await buildingsApi.info({ uuid, _case: caseUuid });
};

export const bulkCreateSites = async (bulkCreateData) => {
  const sites = bulkCreateData.map((siteAssetData) => ({
    aosItem: siteAssetData.aosItem,
    path: siteAssetData.path,
  }));
  const response = await sitesApi.bulkCreate({ siteBulkCreateRequest: { sites } });
  return response.results;
};

export const retrieveBuilding = async (assetBuildingUuid) => {
  const response = await buildingsApi.retrieve({ uuid: assetBuildingUuid });
  return buildingFromSdk(response);
};

export const updateBuilding = async (assetBuildingData) => {
  const patchedBuilding = sdkStringsToDates(assetBuildingData, BUILDING_DATE_FIELDS);
  const response = await buildingsApi.partialUpdate({
    uuid: assetBuildingData.uuid,
    patchedBuildingRequest: patchedBuilding,
  });
  return buildingFromSdk(response);
};

export const fetchStoreys = async (params = {}) => {
  const listParams = toAosListParams(params);
  return listAllPages((page) => storeysApi.list({ ...listParams, page }));
};

export const fetchSpaces = async (params = {}) => {
  const listParams = toAosListParams(params);
  return listAllPages((page) => spacesApi.list({ ...listParams, page }));
};

export const fetchZones = async (params = {}) => {
  const listParams = toAosListParams(params);
  return listAllPages((page) => zonesApi.list({ ...listParams, page }));
};

export const retrieveZone = async (assetZoneUuid) => {
  return await zonesApi.retrieve({ uuid: assetZoneUuid });
};

export const updateZone = async (assetZoneData) => {
  const { uuid, ...patchedZone } = assetZoneData;
  return await zonesApi.partialUpdate({ uuid, patchedZoneRequest: patchedZone });
};

export const duplicateZones = async (sourceZone, targetZones, fieldsToDuplicate) => {
  await zonesApi.duplicate({
    zoneDuplicationRequest: {
      sourceZone,
      targetZones,
      fieldsToDuplicate: fieldsToDuplicate.map((key) => camelToSnakeCase(key)),
    },
  });
};
